/**
 * WEB-809 — gate internal `/venue/{slug}` and `/artist/{slug}` links on a
 * slug that can actually resolve. Artist pages 404 without a slug (and
 * `/artist/{id}` is not a lookup); venue crumbs must never emit
 * `/venue/venue` (WEB-328).
 *
 * The shape matches `@loci/validation`'s `venueSlugSchema` (lowercase
 * `[a-z0-9-]`, 1–120 chars). Artist slugs use the same URL grammar, so one
 * helper covers both destinations. Placeholder tokens (`venue`, `artist`)
 * pass the regex but name dead routes — reject them by identity.
 */

import { venueSlugSchema } from '@loci/validation';

const PLACEHOLDER_SLUGS: ReadonlySet<string> = new Set(['venue', 'artist']);

/**
 * Trim-and-validate a page slug. Whitespace, empty, placeholder, and
 * non-URL-safe values all collapse to `null` — callers render plain text.
 */
export function readResolvablePageSlug(raw: unknown): string | null {
  if (typeof raw !== 'string') {
    return null;
  }
  const trimmed = raw.trim();
  if (trimmed.length === 0 || PLACEHOLDER_SLUGS.has(trimmed)) {
    return null;
  }
  const parsed = venueSlugSchema.safeParse(trimmed);
  return parsed.success ? parsed.data : null;
}

/** The venue-identity extras the event-detail payload actually ships. */
export interface LocationSlugSource {
  /** Canonical `Location.slug` (WEB-809 / OpenAPI `Location.slug`). */
  slug?: string | null;
  /**
   * Pre-WEB-809 extra field fixtures still set. Prefer `slug`; keep this as
   * a fallback so Storybook / RTL doubles keep linking without a regen.
   */
  venueSlug?: string | null;
}

/**
 * Accepts a location-like record. Canonical `slug` wins; `venueSlug` is the
 * legacy extra-field fallback (declared on OpenAPI `Location` since API-1720).
 */
export function readLocationVenueSlug(location: unknown): string | null {
  if (location === null || location === undefined || typeof location !== 'object') {
    return null;
  }
  const record = location as LocationSlugSource;
  return readResolvablePageSlug(record.slug) ?? readResolvablePageSlug(record.venueSlug);
}
